import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Send, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Reveal } from "@/components/Reveal";
import { enviarResena, listarResenas } from "@/lib/api";

const VACIO = { nombre: "", estrellas: 0, comentario: "" };

const Estrellas = ({ valor, size = "h-4 w-4" }) => (
  <div className="flex gap-0.5" aria-label={`${valor} de 5 estrellas`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`${size} ${n <= valor ? "fill-dorado text-dorado" : "text-arena"}`}
      />
    ))}
  </div>
);

export const Resenas = () => {
  const [resenas, setResenas] = useState([]);
  const [form, setForm] = useState(VACIO);
  const [hover, setHover] = useState(0);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    listarResenas()
      .then((r) => setResenas(r.data))
      .catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.estrellas) {
      setStatus("error");
      setErrorMsg("Selecciona una valoración de 1 a 5 estrellas.");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      await enviarResena(form);
      const r = await listarResenas();
      setResenas(r.data);
      setStatus("success");
      setForm(VACIO);
      setHover(0);
      toast.success("¡Gracias por tu opinión! Se ha publicado correctamente.");
    } catch {
      setStatus("error");
      setErrorMsg("No se pudo enviar la opinión. Revisa los campos e inténtalo de nuevo.");
    }
  };

  return (
    <section id="opiniones" data-testid="reviews-section" className="bg-crema/40 py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <Reveal>
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-cafe">
            <span aria-hidden className="h-px w-9 bg-cafe/60" />
            Opiniones
          </p>
          <h2 className="mt-6 max-w-2xl font-serif text-3xl leading-[1.14] tracking-tight text-tinta sm:text-4xl lg:text-[2.9rem]">
            Opiniones de{" "}
            <em className="font-garamond font-medium italic text-cafe">nuestros clientes.</em>
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-12 lg:gap-14">
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="rounded-3xl border border-arena/70 bg-white p-6 shadow-[0_18px_40px_rgba(58,36,24,0.08)] sm:p-8">
              {status === "success" ? (
                <div
                  data-testid="review-form-success"
                  className="flex min-h-[320px] flex-col items-center justify-center text-center"
                >
                  <CheckCircle2 className="h-14 w-14 text-green-700" strokeWidth={1.4} />
                  <h3 className="mt-6 font-serif text-2xl text-tinta">¡Gracias por tu opinión!</h3>
                  <p className="mt-3 max-w-xs text-sm leading-relaxed text-tinta/65">
                    Hemos recibido tu valoración y ya aparece publicada en esta página.
                  </p>
                  <Button
                    variant="outline"
                    data-testid="review-form-success-reset"
                    onClick={() => setStatus("idle")}
                    className="mt-8 rounded-full px-7"
                  >
                    Escribir otra opinión
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5">
                  <h3 className="font-serif text-2xl text-tinta">Comparte tu experiencia</h3>
                  <div className="space-y-2">
                    <Label>Tu valoración</Label>
                    <div
                      className="flex gap-1.5"
                      role="radiogroup"
                      aria-label="Valoración de 1 a 5 estrellas"
                    >
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          data-testid={`review-star-${n}`}
                          onClick={() => setForm((f) => ({ ...f, estrellas: n }))}
                          onMouseEnter={() => setHover(n)}
                          onMouseLeave={() => setHover(0)}
                          aria-label={`${n} estrella${n > 1 ? "s" : ""}`}
                          className="rounded-full p-1 transition-transform hover:scale-110"
                        >
                          <Star
                            className={`h-7 w-7 transition-colors ${
                              n <= (hover || form.estrellas)
                                ? "fill-dorado text-dorado"
                                : "text-arena"
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rv-nombre">Nombre</Label>
                    <Input
                      id="rv-nombre"
                      data-testid="review-form-nombre"
                      value={form.nombre}
                      onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                      placeholder="Tu nombre"
                      required
                      minLength={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rv-comentario">Tu comentario</Label>
                    <Textarea
                      id="rv-comentario"
                      data-testid="review-form-comentario"
                      value={form.comentario}
                      onChange={(e) => setForm((f) => ({ ...f, comentario: e.target.value }))}
                      placeholder="Cuéntanos qué te ha parecido"
                      rows={4}
                      required
                      minLength={5}
                    />
                  </div>
                  {status === "error" && (
                    <p
                      data-testid="review-form-error"
                      role="alert"
                      className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive"
                    >
                      {errorMsg}
                    </p>
                  )}
                  <Button
                    type="submit"
                    data-testid="review-form-submit"
                    disabled={status === "sending"}
                    className="w-full rounded-full py-6 text-base sm:w-auto sm:px-10"
                  >
                    {status === "sending" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Enviar opinión
                  </Button>
                </form>
              )}
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            {resenas.length === 0 ? (
              <Reveal
                delay={0.15}
                data-testid="reviews-empty"
                className="flex h-full min-h-[280px] flex-col items-center justify-center rounded-3xl border border-dashed border-arena bg-fondo/60 p-10 text-center"
              >
                <Star className="h-10 w-10 text-arena" strokeWidth={1.2} />
                <p className="mt-5 max-w-sm font-serif text-xl text-tinta/70">
                  Todavía no hay opiniones publicadas.
                </p>
                <p className="mt-2 max-w-sm text-sm text-tinta/55">
                  Sé la primera persona en compartir tu experiencia con Rawan.
                </p>
              </Reveal>
            ) : (
              <div data-testid="reviews-list" className="grid gap-5 sm:grid-cols-2">
                {resenas.map((r, i) => (
                  <Reveal
                    key={r.id || i}
                    delay={Math.min(i, 5) * 0.08}
                    data-testid={`review-card-${i}`}
                  >
                    <figure className="flex h-full flex-col rounded-3xl border border-arena/70 bg-white p-6 shadow-sm">
                      <Estrellas valor={r.estrellas} />
                      <blockquote className="mt-4 flex-1 font-garamond text-lg italic leading-relaxed text-tinta/80">
                        “{r.comentario}”
                      </blockquote>
                      <figcaption className="mt-5 flex items-center gap-3">
                        <span
                          aria-hidden
                          className="grid h-9 w-9 place-items-center rounded-full bg-chocolate font-serif text-sm text-crema"
                        >
                          {r.nombre.trim().charAt(0).toUpperCase()}
                        </span>
                        <span className="text-sm font-semibold text-tinta">{r.nombre}</span>
                      </figcaption>
                    </figure>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Resenas;
